import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { BRAND_PARAM } from './brandParam';
import { useAccount } from './useAccount';

/**
 * Applies `?brand=<business_id>` and then drops it from the address (see
 * `brandParam.ts`). Renders nothing. Mounted once inside the router, because
 * `AccountProvider` sits above it and cannot navigate.
 *
 * It waits for a signed-in user's account to load, so the brand list is
 * known; a brand the user is not in is ignored and the param is still removed.
 * Signed out, it leaves the param alone, so the sign-in redirect carries it
 * through to the page after sign-in.
 */
export function BrandFromUrl() {
  const { user } = useAuth();
  const { businesses, isLoading, setActiveBusinessId } = useAccount();
  const isSignedIn = user !== null;
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isSignedIn || isLoading) return;
    const params = new URLSearchParams(location.search);
    const linked = params.get(BRAND_PARAM);
    if (linked === null) return;
    if (businesses.some((entry) => entry.id === linked)) setActiveBusinessId(linked);
    params.delete(BRAND_PARAM);
    const search = params.toString();
    void navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
      { replace: true, state: location.state as unknown },
    );
  }, [
    isSignedIn,
    isLoading,
    businesses,
    location.pathname,
    location.search,
    location.hash,
    location.state,
    navigate,
    setActiveBusinessId,
  ]);

  return null;
}
